import httpClient from './httpClient';
import { COMPONENT_PROJECTS } from '../config/componentProjects';
import {
  AdminFinancialsListItem,
  AdminFinancialsListResponse,
  ComponentProjectFinancials,
  ComponentProjectFinancialsItem,
  ComponentProjectTcfMatchMonth,
  FinancialAsOfPeriod,
  FinancialMonthSubmission,
  FinancialsProfile,
  FinancialsSummary,
  GetAdminFinancialsParams,
  UpsertComponentProjectTcfMatchResult,
  UpsertMonthSpendInput,
  UpsertMonthSpendResult,
} from '../types/financials';

const adminScopeHeaders = {
  headers: { 'X-Admin-Scope': 'all' },
};

type ApiAwardWindow = {
  start_date: string;
  end_date: string;
};

type ApiSpendTargets = {
  federal_target: number;
  match_target: number;
};

type ApiMonthSubmission = {
  year: number;
  month: number;
  federal_spend_to_date: number | null;
  match_spend_to_date: number | null;
  earned_revenue_to_date: number | null;
  updated_at: string;
  updated_by: string;
};

type ApiFinancialsProfile = {
  subawardee_id: string;
  award_window: ApiAwardWindow;
  spend_targets: ApiSpendTargets;
  submissions: ApiMonthSubmission[];
};

type ApiFinancialsSummary = {
  federal_spend_to_date: number | null;
  match_spend_to_date: number | null;
  earned_revenue_to_date: number | null;
  federal_as_of: ApiAsOfPeriod | null;
  match_as_of: ApiAsOfPeriod | null;
  earned_revenue_as_of: ApiAsOfPeriod | null;
  giveback_risk: number;
  spend_targets: ApiSpendTargets;
  award_window: ApiAwardWindow;
};

type ApiUpsertDeleted = {
  deleted: true;
  year: number;
  month: number;
};

type ApiAsOfPeriod = {
  year: number;
  month: number;
};

type ApiComponentProjectFinancialsItem = {
  subawardee_id: string;
  award_window: ApiAwardWindow;
  spend_targets: ApiSpendTargets;
  federal_spend_to_date: number | null;
  match_spend_to_date: number | null;
  earned_revenue_to_date: number | null;
  federal_as_of: ApiAsOfPeriod | null;
  match_as_of: ApiAsOfPeriod | null;
  earned_revenue_as_of: ApiAsOfPeriod | null;
  giveback_risk: number;
  last_updated_at: string | null;
  last_updated_by: string | null;
};

type ApiComponentProjectFinancials = {
  component_project_id: string;
  federal_budget: number | null;
  tcf_match_budget: number | null;
  subawardee_match_budget: number | null;
  tcf_match_to_date: number | null;
  tcf_match_as_of: ApiAsOfPeriod | null;
  items: ApiComponentProjectFinancialsItem[];
  federal_spend_to_date: number | null;
  match_spend_to_date: number | null;
  earned_revenue_to_date: number | null;
  giveback_risk?: number | null;
  federal_target?: number | null;
  match_target?: number | null;
};

type ApiComponentProjectTcfMatchMonth = {
  year: number;
  month: number;
  tcf_match_to_date: number | null;
  updated_at: string;
  updated_by: string;
};

type ApiAdminFinancialListRow = ApiComponentProjectFinancialsItem & {
  submissions: ApiMonthSubmission[];
};

type ApiAdminFinancialsListResponse = {
  items: ApiAdminFinancialListRow[];
  pagination: {
    page: number;
    page_size: number;
    total_items: number;
    total_pages: number;
    has_next: boolean;
    has_prev: boolean;
  };
  filters_applied: Record<string, unknown> | null;
};

function mapSubmission(entry: ApiMonthSubmission): FinancialMonthSubmission {
  return {
    year: entry.year,
    month: entry.month,
    federalSpendToDate: entry.federal_spend_to_date,
    matchSpendToDate: entry.match_spend_to_date,
    earnedRevenueToDate: entry.earned_revenue_to_date ?? null,
    updatedAt: entry.updated_at,
    updatedBy: entry.updated_by,
  };
}

function mapAwardWindow(window: ApiAwardWindow): FinancialsProfile['awardWindow'] {
  return {
    startDate: window.start_date,
    endDate: window.end_date,
  };
}

function mapSpendTargets(targets: ApiSpendTargets): FinancialsProfile['spendTargets'] {
  return {
    federalTarget: targets.federal_target,
    matchTarget: targets.match_target,
  };
}

function mapProfile(data: ApiFinancialsProfile): FinancialsProfile {
  return {
    subawardeeId: data.subawardee_id,
    awardWindow: mapAwardWindow(data.award_window),
    spendTargets: mapSpendTargets(data.spend_targets),
    submissions: (data.submissions ?? []).map(mapSubmission),
  };
}

function mapAsOf(period: ApiAsOfPeriod | null | undefined): FinancialAsOfPeriod | null {
  if (!period || typeof period.year !== 'number' || typeof period.month !== 'number') {
    return null;
  }
  return { year: period.year, month: period.month };
}

/** Coerce API numerics (number | numeric string) to a finite number, else null. */
function mapOptionalNumber(value: unknown): number | null {
  if (value == null || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

function mapSummary(data: ApiFinancialsSummary): FinancialsSummary {
  return {
    federalSpendToDate: data.federal_spend_to_date,
    matchSpendToDate: data.match_spend_to_date,
    earnedRevenueToDate: data.earned_revenue_to_date ?? null,
    federalAsOf: mapAsOf(data.federal_as_of),
    matchAsOf: mapAsOf(data.match_as_of),
    earnedRevenueAsOf: mapAsOf(data.earned_revenue_as_of),
    givebackRisk: mapGivebackRisk(data) ?? 0,
    spendTargets: mapSpendTargets(data.spend_targets),
    awardWindow: mapAwardWindow(data.award_window),
  };
}

/** Read giveback_risk from API; default 0 for item/summary rows that always send a number. */
function mapGivebackRisk(
  data: { giveback_risk?: number | null },
  { optional = false }: { optional?: boolean } = {}
): number | null {
  const mapped = mapOptionalNumber(data.giveback_risk);
  if (mapped !== null) return mapped;
  return optional ? null : 0;
}

function mapComponentProjectItem(
  entry: ApiComponentProjectFinancialsItem
): ComponentProjectFinancialsItem {
  return {
    subawardeeId: entry.subawardee_id,
    awardWindow: mapAwardWindow(entry.award_window),
    spendTargets: mapSpendTargets(entry.spend_targets),
    federalSpendToDate: entry.federal_spend_to_date,
    matchSpendToDate: entry.match_spend_to_date,
    earnedRevenueToDate: entry.earned_revenue_to_date ?? null,
    federalAsOf: mapAsOf(entry.federal_as_of),
    matchAsOf: mapAsOf(entry.match_as_of),
    earnedRevenueAsOf: mapAsOf(entry.earned_revenue_as_of),
    givebackRisk: mapGivebackRisk(entry) ?? 0,
    lastUpdatedAt: entry.last_updated_at,
    lastUpdatedBy: entry.last_updated_by,
  };
}

function mapComponentProjectFinancials(
  data: ApiComponentProjectFinancials
): ComponentProjectFinancials {
  return {
    componentProjectId: data.component_project_id,
    federalBudget: mapOptionalNumber(data.federal_budget),
    tcfMatchBudget: mapOptionalNumber(data.tcf_match_budget),
    subawardeeMatchBudget: mapOptionalNumber(data.subawardee_match_budget),
    tcfMatchToDate: mapOptionalNumber(data.tcf_match_to_date),
    tcfMatchAsOf: mapAsOf(data.tcf_match_as_of),
    items: (data.items ?? []).map(mapComponentProjectItem),
    federalSpendToDate: mapOptionalNumber(data.federal_spend_to_date),
    matchSpendToDate: mapOptionalNumber(data.match_spend_to_date),
    earnedRevenueToDate: mapOptionalNumber(data.earned_revenue_to_date),
    givebackRisk: mapGivebackRisk(data, { optional: true }),
    federalTarget: mapOptionalNumber(data.federal_target),
    matchTarget: mapOptionalNumber(data.match_target),
  };
}

function mapTcfMatchMonth(entry: ApiComponentProjectTcfMatchMonth): ComponentProjectTcfMatchMonth {
  return {
    year: entry.year,
    month: entry.month,
    tcfMatchToDate: entry.tcf_match_to_date,
    updatedAt: entry.updated_at,
    updatedBy: entry.updated_by,
  };
}

function mapAdminFinancialListRow(entry: ApiAdminFinancialListRow): AdminFinancialsListItem {
  return {
    ...mapComponentProjectItem(entry),
    submissions: (entry.submissions ?? []).map(mapSubmission),
  };
}

function mapAdminFinancialsList(
  data: ApiAdminFinancialsListResponse
): AdminFinancialsListResponse {
  return {
    items: (data.items ?? []).map(mapAdminFinancialListRow),
    pagination: {
      page: data.pagination.page,
      pageSize: data.pagination.page_size,
      totalItems: data.pagination.total_items,
      totalPages: data.pagination.total_pages,
      hasNext: data.pagination.has_next,
      hasPrev: data.pagination.has_prev,
    },
    filtersApplied: data.filters_applied ?? null,
  };
}

function isDeletedResult(data: unknown): data is ApiUpsertDeleted {
  return (
    !!data &&
    typeof data === 'object' &&
    (data as ApiUpsertDeleted).deleted === true &&
    typeof (data as ApiUpsertDeleted).year === 'number' &&
    typeof (data as ApiUpsertDeleted).month === 'number'
  );
}

export async function getFinancials(): Promise<FinancialsProfile> {
  try {
    const response = await httpClient.get<ApiFinancialsProfile>('/v1/financials/');
    return mapProfile(response.data);
  } catch (error) {
    console.error('Error fetching financials:', error);
    throw error;
  }
}

export async function getFinancialsForSubawardee(
  subawardee: string
): Promise<FinancialsProfile> {
  try {
    const response = await httpClient.get<ApiFinancialsProfile>('/v1/financials/', {
      headers: { 'X-Selected-Group': subawardee },
    });
    return mapProfile(response.data);
  } catch (error) {
    console.error('Error fetching financials for subawardee:', error);
    throw error;
  }
}

export async function getFinancialsSummary(subawardee?: string): Promise<FinancialsSummary> {
  try {
    const response = await httpClient.get<ApiFinancialsSummary>('/v1/financials/summary', {
      headers: subawardee ? { 'X-Selected-Group': subawardee } : undefined,
    });
    return mapSummary(response.data);
  } catch (error) {
    console.error('Error fetching financials summary:', error);
    throw error;
  }
}

/** Paginated cross-org subawardee financials (profiles + overview). */
export async function getAdminFinancials(
  params: GetAdminFinancialsParams = {}
): Promise<AdminFinancialsListResponse> {
  try {
    const response = await httpClient.get<ApiAdminFinancialsListResponse>('/v1/admin/financials', {
      params: {
        page: params.page ?? 1,
        page_size: params.pageSize ?? 25,
        sort: params.sort ?? 'subawardee-asc',
        ...(params.subawardee ? { subawardee: params.subawardee } : {}),
      },
      ...adminScopeHeaders,
    });
    return mapAdminFinancialsList(response.data);
  } catch (error) {
    console.error('Error fetching admin financials:', error);
    throw error;
  }
}

/** Program budgets + rolled-up subawardee spend for one component project. */
export async function getComponentProjectFinancials(
  componentProjectId: string
): Promise<ComponentProjectFinancials> {
  try {
    const response = await httpClient.get<ApiComponentProjectFinancials>(
      `/v1/admin/component-projects/${encodeURIComponent(componentProjectId)}/financials`,
      adminScopeHeaders
    );
    return mapComponentProjectFinancials(response.data);
  } catch (error) {
    console.error('Error fetching component project financials:', error);
    throw error;
  }
}

/** Fetch financials for every configured component project (parallel). */
export async function getAllComponentProjectFinancials(): Promise<ComponentProjectFinancials[]> {
  return Promise.all(
    COMPONENT_PROJECTS.map((project) => getComponentProjectFinancials(project.id))
  );
}

export async function upsertMonthSpend(
  year: number,
  month: number,
  input: UpsertMonthSpendInput,
  subawardee?: string
): Promise<UpsertMonthSpendResult> {
  try {
    const body: Record<string, number | null> = {};
    if (Object.prototype.hasOwnProperty.call(input, 'federalSpendToDate')) {
      body.federal_spend_to_date = input.federalSpendToDate ?? null;
    }
    if (Object.prototype.hasOwnProperty.call(input, 'matchSpendToDate')) {
      body.match_spend_to_date = input.matchSpendToDate ?? null;
    }
    if (Object.prototype.hasOwnProperty.call(input, 'earnedRevenueToDate')) {
      body.earned_revenue_to_date = input.earnedRevenueToDate ?? null;
    }

    const response = await httpClient.put<ApiMonthSubmission | ApiUpsertDeleted>(
      `/v1/financials/spend/${year}/${month}`,
      body,
      subawardee ? { headers: { 'X-Selected-Group': subawardee } } : undefined
    );

    if (isDeletedResult(response.data)) {
      return {
        deleted: true,
        year: response.data.year,
        month: response.data.month,
      };
    }

    return mapSubmission(response.data);
  } catch (error) {
    console.error('Error upserting financial month spend:', error);
    throw error;
  }
}

/** Upsert cumulative TCF match spend for one month at component-project scope (admin only). */
export async function upsertComponentProjectTcfMatch(
  componentProjectId: string,
  year: number,
  month: number,
  tcfMatchToDate: number | null
): Promise<UpsertComponentProjectTcfMatchResult> {
  try {
    const response = await httpClient.put<ApiComponentProjectTcfMatchMonth | ApiUpsertDeleted>(
      `/v1/admin/component-projects/${encodeURIComponent(componentProjectId)}/financials/tcf-match/${year}/${month}`,
      { tcf_match_to_date: tcfMatchToDate },
      adminScopeHeaders
    );

    if (isDeletedResult(response.data)) {
      return {
        deleted: true,
        year: response.data.year,
        month: response.data.month,
      };
    }

    return mapTcfMatchMonth(response.data);
  } catch (error) {
    console.error('Error upserting component project TCF match:', error);
    throw error;
  }
}

export const financialsApi = {
  getFinancials,
  getFinancialsForSubawardee,
  getFinancialsSummary,
  getAdminFinancials,
  getComponentProjectFinancials,
  getAllComponentProjectFinancials,
  upsertMonthSpend,
  upsertComponentProjectTcfMatch,
};
